import {FC} from 'react'
import {Form} from 'react-bootstrap'
import {AgreementDetail} from '../core/_models'
import {getISO2ByISO3} from '../../../../libs/misc'
import '../TradeDataAgreementDetailPage.scss'

import {ColumnDef, createColumnHelper} from '@tanstack/react-table'
import DataTable from '../../../../../ui-kit/DataTable'

type Props = {
  agreementDetail: AgreementDetail
  idAgreement?: string
  region?: string
}

type AgreementSource = {
  id?: string
  alias: string
  link: string
}

const renderValue = (value?: string) => {
  if (!value || value === '') return '-'
  return value
}

const columnHelper = createColumnHelper<AgreementSource>()

const sourceColumns: ColumnDef<AgreementSource, any>[] = [
  columnHelper.accessor('alias', {
    header: 'Alias',
    cell: (info) => info.getValue() || '-',
    meta: {
      headerClass: 'text-start',
      cellClass: 'text-start',
    },
  }),
  columnHelper.accessor('link', {
    header: 'Link',
    cell: (info) => {
      const link = info.getValue()
      return link ? (
        <a href={link} target='_blank' rel='noreferrer'>
          {link}
        </a>
      ) : (
        '-'
      )
    },
    meta: {
      headerClass: 'text-start',
      cellClass: 'text-start',
    },
  }),
]

const AgreementDetailView: FC<Props> = ({agreementDetail, idAgreement, region}) => {
  const sourcesView = Array.isArray(agreementDetail.sources) ? agreementDetail.sources : []

  return (
    <div className='detail-agreement'>
      <h3>General Information</h3>
      <div className='mb-3'>
        <Form.Label>Name</Form.Label>
        <div className='data-value'>{renderValue(agreementDetail.agreement)}</div>
      </div>

      <div className='row'>
        <div className='col-md-4 mb-3'>
          <Form.Label>Short Name</Form.Label>
          <div className='data-value'>{renderValue(agreementDetail.acronym)}</div>
        </div>

        <div className='col-md-4 mb-3'>
          <Form.Label>Treaty Identifier TINA</Form.Label>
          <div className='data-value'>{renderValue(idAgreement)}</div>
        </div>

        <div className='col-md-4 mb-3'>
          <Form.Label>Type</Form.Label>
          <div className='data-value'>
            {agreementDetail.type?.length
              ? agreementDetail.type.map((type, idx) => (
                  <span key={idx} className='badge bg-dark me-1'>
                    {type.trim()}
                  </span>
                ))
              : '-'}
          </div>
        </div>

        <div className='col-md-4 mb-3'>
          <Form.Label>Status</Form.Label>
          <div className='data-value'>{renderValue(agreementDetail.status)}</div>
        </div>

        <div className='col-md-4 mb-3'>
          <Form.Label>WTO RTA Id</Form.Label>
          <div className='data-value'>{renderValue(agreementDetail.wto_rta_id)}</div>
        </div>

        <div className='col-md-4 mb-3'>
          <Form.Label>Notification</Form.Label>
          <div className='data-value'>
            {agreementDetail.notification?.length
              ? agreementDetail.notification.map((n, idx) => (
                  <span key={idx} className='badge bg-dark me-1'>
                    {n.trim()}
                  </span>
                ))
              : '-'}
          </div>
        </div>
      </div>

      <hr />
      <h3>Date Information</h3>
      <div className='row'>
        {[
          ['date_signed', 'Date Signed'],
          ['date_into_force', 'Date Into Force'],
          ['date_notification', 'Date Notification'],
          ['end_implementation', 'End Implementation'],
          ['date_inactive', 'Date Inactive'],
        ].map(([key, label]) => (
          <div className='col-md-4 mb-3' key={key}>
            <Form.Label>{label}</Form.Label>
            <div className='data-value'>{renderValue((agreementDetail as any)[key])}</div>
          </div>
        ))}
      </div>

      <hr />
      <h3>Participation & Scope</h3>
      <div className='row'>
        <div className='col-md-4 mb-3'>
          <Form.Label>Parties</Form.Label>
          <div className='data-value'>
            {agreementDetail.iso?.length
              ? agreementDetail.iso.map((rawIso, idx) => {
                  const iso = rawIso.trim()
                  const iso2 = getISO2ByISO3(iso)

                  return !iso2 ? (
                    <span key={idx} className='badge bg-dark me-1'>
                      {iso}
                    </span>
                  ) : (
                    <span key={idx} className={`fi fi-${iso2.toLowerCase()} me-2`} title={iso} />
                  )
                })
              : '-'}
          </div>
        </div>

        <div className='col-md-4 mb-3'>
          <Form.Label>Composition</Form.Label>
          <div className='data-value'>{renderValue(agreementDetail.composition)}</div>
        </div>

        <div className='col-md-4 mb-3'>
          <Form.Label>Parties WTO</Form.Label>
          <div className='data-value text-capitalize'>
            {renderValue(agreementDetail.parties_wto)}
          </div>
        </div>

        <div className='col-md-4 mb-3'>
          <Form.Label>Region</Form.Label>
          <div className='data-value'>
            {region
              ? region.split(';').map((r, idx) => (
                  <span key={idx} className='badge bg-dark me-1 break-word'>
                    {r.trim()}
                  </span>
                ))
              : '-'}
          </div>
        </div>

        <div className='col-md-4 mb-3'>
          <Form.Label>Subregion</Form.Label>
          <div className='data-value'>
            {agreementDetail.subregion
              ? agreementDetail.subregion.split(';').map((r, idx) => (
                  <span key={idx} className='badge bg-dark me-1 break-word'>
                    {r.trim()}
                  </span>
                ))
              : '-'}
          </div>
        </div>

        <div className='col-md-4 mb-3'>
          <Form.Label>Crossregional</Form.Label>
          <div className='data-value text-capitalize'>
            {renderValue(agreementDetail.crossregional)}
          </div>
        </div>

        <div className='col-md-4 mb-3'>
          <Form.Label>Parties ESCAP</Form.Label>
          <div className='data-value text-capitalize'>
            {renderValue(agreementDetail.parties_escap)}
          </div>
        </div>
      </div>

      <hr />
      <h3>Others</h3>

      <div className='row'>
        <div className='col-md-4 mb-3'>
          <Form.Label>Language</Form.Label>
          <div className='data-value'>{renderValue(agreementDetail.language)}</div>
        </div>

        <div className='col-md-4 mb-3'>
          <Form.Label>Service Schedule</Form.Label>
          <div className='data-value'>
            {agreementDetail.services_schedules?.toLowerCase() === 'yes' ? 'Yes' : 'No'}
          </div>
        </div>
      </div>

      <hr />
      <h3>Sources</h3>

      <div className='row'>
        <DataTable<AgreementSource>
          data={sourcesView}
          columns={sourceColumns}
          enablePagination={false}
          enableSorting={false}
          enableGlobalFilter={false}
          className='my-custom-table-wrapper'
        />
      </div>
    </div>
  )
}

export default AgreementDetailView
